import LoginLayout from '@/layout/LoginLayout';
import { trpc } from '@/trpcClient';
import {
  checkUpdateContactInfoInput,
  UpdateContactInfoInputType,
} from '@shelflix/schemas/authentication/updateContactInfo';
import Icon from '@shelflix/ui-web/components/Icon';
import { Button } from '@shelflix/ui-web/components/ui/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  scrollToError,
} from '@shelflix/ui-web/components/ui/form';
import { Input } from '@shelflix/ui-web/components/ui/input';
import { useMutation } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { extractTenantSlug, getTenantDomain } from '@/utils/tenantDetection';

export default function LoginUpdateContactInfo() {
  const updateContactInfoMutation = useMutation(trpc.authentication['update-contact-info'].mutationOptions());
  const verificationCodeReceiveMutation = useMutation(
    trpc.authentication['verification-code-receive'].mutationOptions()
  );
  const [searchParams] = useSearchParams();
  const registration = searchParams.get('registration');
  const navigate = useNavigate();

  const form = useForm<UpdateContactInfoInputType>({
    defaultValues: {
      registration: registration || '',
      email: '',
      phone: '',
    },
    mode: 'onSubmit',
  });

  const onSubmit = (data: UpdateContactInfoInputType) => {
    if (updateContactInfoMutation.isPending) return;

    // Get tenant slug from current domain for multi-tenant isolation
    const tenantSlug = extractTenantSlug(getTenantDomain());

    updateContactInfoMutation.mutate(
      {
        email: data.email,
        phone: data.phone?.replaceAll(/[^\d]/g, '') || undefined,
        registration: data.registration,
        tenantSlug,
      },
      {
        onSuccess: async () => {
          const payload = {
            email: data.email,
            tenantSlug,
          };

          verificationCodeReceiveMutation.mutate(payload, {
            onSuccess: async () => {
              navigate(`/verification-receive?firstAccess&email=${data.email}`, {
                state: {
                  sendCode: true,
                },
              });
            },
            onError: () => {
              navigate(`/verification-receive?firstAccess&email=${data.email}`, {
                state: {
                  sendCode: false,
                },
              });
            },
          });
        },
        onError: (error) => {
          toast.error(`Erro ao atualizar informações: ${error.message}`);
        },
      }
    );
  };

  return (
    <LoginLayout>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit, scrollToError)} className="flex flex-col gap-4">
          <div className="mb-4 text-center">
            <h2 className="text-lg font-bold text-[var(--primary)]">Configurar informações de contato</h2>
            <div className="mx-auto mt-4 max-w-xs text-sm text-gray-700">
              Para continuar com o primeiro acesso, informe seu e-mail e telefone:
            </div>
          </div>

          <FormField
            control={form.control}
            name="email"
            rules={{ validate: (value) => checkUpdateContactInfoInput('email', value?.toLowerCase()) }}
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-sm font-medium text-gray-700">E-mail</FormLabel>
                <FormControl>
                  <div className="relative mt-1 flex items-center">
                    <span className="material-symbols-outlined pointer-events-none absolute left-3 text-base text-gray-400">
                      email
                    </span>
                    <Input type="email" id="email" placeholder="Digite seu e-mail" className="pl-9" {...field} />
                  </div>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="phone"
            rules={{ validate: (value) => checkUpdateContactInfoInput('phone', value?.replaceAll(/[^\d]/g, '')) }}
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-sm font-medium text-gray-700">Telefone</FormLabel>
                <FormControl>
                  <div className="relative mt-1 flex items-center">
                    <span className="material-symbols-outlined pointer-events-none absolute left-3 text-base text-gray-400">
                      phone
                    </span>
                    <Input
                      mask="(99) 99999-9999"
                      id="phone"
                      placeholder="(__) _____-____"
                      className="pl-9"
                      {...field}
                    />
                  </div>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <Button
            type="submit"
            loading={updateContactInfoMutation.isPending || verificationCodeReceiveMutation.isPending}
            className="w-full bg-[var(--primary)]"
          >
            {updateContactInfoMutation.isPending || verificationCodeReceiveMutation.isPending ? (
              <div className="flex items-center justify-center space-x-2">
                <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                <span>Salvando...</span>
              </div>
            ) : (
              <>
                <Icon name="save" className="mr-2" />
                Salvar e continuar
              </>
            )}
          </Button>

          <Button
            type="button"
            variant="outline"
            className="w-full border-[var(--primary)] text-[var(--primary)] hover:bg-[var(--secondary)]"
            onClick={() => navigate('/verification-receive?firstAccess')}
          >
            Voltar
          </Button>

          <div className="h-10 w-full" />
        </form>
      </Form>
    </LoginLayout>
  );
}
